import { useEffect, useState } from 'react'
import moment from 'moment'
import { useParams } from 'react-router-dom'
import { useSelector } from 'store/store'
import { toast } from 'react-toastify'
import { Skeleton } from '@mui/material'
import { useDispatch } from 'store/store'
import { Controller, useForm } from 'react-hook-form'
import { SlimButton, DatePicker, SectionCard } from 'components'
import { usePermissions } from 'commons/customHooks/useAuth'
import { ActionPerm } from 'components/Structures/Header/constants'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { oVMaintenance } from 'services/manage_ov'
import { OvMaintenanceResponse } from 'services/manage_ov/interface'
import { setOvDetailedInfo } from 'store/OVContext/ovSlice'
import { getUser } from 'commons/utils/user'

import * as S from './styles'

export const BillingSection = () => {
  const {
    ovSelected: { Cabecalho },
    detailedInfo,
    detailedInfo: { FaturamentoEntrega },
    loadingDetailedInfo: loading
  } = useSelector((state) => state.ov)

  const { ovNumber } = useParams()
  const dispatch = useDispatch()
  const [loadingSave, setLoadingSave] = useState(false)

  const defaultValues = {
    prevFaturamento: FaturamentoEntrega?.DataPrevisaoFaturamento,
    dtDesejadaCliente: FaturamentoEntrega?.DataDesejadaCliente
  }

  const {
    control,
    watch,
    formState: { isDirty },
    handleSubmit,
    reset
  } = useForm({
    defaultValues
  })

  const currentPrevDate = watch('prevFaturamento')

  const resetForm = () => {
    reset({
      prevFaturamento: FaturamentoEntrega.DataPrevisaoFaturamento,
      dtDesejadaCliente: FaturamentoEntrega.DataDesejadaCliente
    })
  }

  useEffect(() => {
    if (!loading) {
      resetForm()
    }
  }, [loading])

  const { verifyPermission } = usePermissions()

  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.OV, action) || +Cabecalho.CodigoStatus !== 0
  }

  const onSave = async (data: typeof defaultValues) => {
    const requestBody = {
      // TODO: Replace momentjs with functions from commons/utils/date
      DataDesejadaCliente: moment(data.dtDesejadaCliente).format('YYYY-MM-DD'),
      DataPrevistaFaturamento: moment(data.prevFaturamento).format('YYYY-MM-DD'),
      NumeroOrdemVenda: `${ovNumber}`,
      CodigoUsuarioAlteracao: getUser(),
      CodigoEntrega: Cabecalho.CodigoSapCliente,
      CondicaoPagamento: Cabecalho.CondicaoPagamento,
      DiasAdicionais: Cabecalho.PrazoExtra,
      NumeroPedidoCliente: Cabecalho.NumeroPedidoCliente,
      TipoOrdem: Cabecalho.CodigoTipoOrdem
    }
    setLoadingSave(true)
    try {
      const res: OvMaintenanceResponse = await oVMaintenance(`${ovNumber}`, requestBody)
      if (res.OperacaoRealizada) {
        dispatch(
          setOvDetailedInfo({
            ...detailedInfo,
            FaturamentoEntrega: {
              ...FaturamentoEntrega,
              DataDesejadaCliente: requestBody.DataDesejadaCliente,
              DataPrevisaoFaturamento: requestBody.DataPrevistaFaturamento
            }
          })
        )
        reset({
          prevFaturamento: requestBody.DataPrevistaFaturamento,
          dtDesejadaCliente: requestBody.DataDesejadaCliente
        })
        toast.success('Ordem de Venda editada com sucesso.')
      } else {
        resetForm()
        toast.error(res?.Mensagem || `Não foi possível editar a Ordem de Venda.`)
      }
    } catch (error) {
      resetForm()
      toast.error(`Erro ao editar a Ordem de Venda.`)
    } finally {
      setLoadingSave(false)
    }
  }

  const RenderSkeleton = () => (
    <S.Wrapper>
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
    </S.Wrapper>
  )

  return (
    <SectionCard
      title="Datas de faturamento e entrega"
      tooltipText="Nesta seção você pode ver as datas calculadas pelo sistema referentes ao faturamento e a entrega da OV. Também é possível programar datas específicas para essas áreas, de acordo com a necessidade do cliente."
    >
      {loading ? (
        <RenderSkeleton />
      ) : (
        <S.Wrapper onSubmit={handleSubmit(onSave)}>
          <Controller
            name="prevFaturamento"
            control={control}
            rules={{
              validate: {
                min: (date) =>
                  moment(date).endOf('day').isAfter(moment().startOf('day')) ||
                  'Data fora do período válido.',
                max: (date) =>
                  moment(date).isBefore(moment().endOf('month')) ||
                  'Data fora do período válido.'
              }
            }}
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                label="Dt. prev. faturamento"
                disabled={shouldDisable(ACTIONS_SCREENS.SAVE)}
                maxDate={moment(field.value).endOf('month') as unknown as string}
                error={fieldState.error?.message}
                disablePast
              />
            )}
          />
          <DatePicker
            label="Entrega calculada"
            value={FaturamentoEntrega.DataEntregaCalculada}
            disabled
            onChange={() => ''}
          />
          <DatePicker
            label="Dt. fatura calculada"
            value={FaturamentoEntrega.DataFaturamentoCalculado}
            disabled
            onChange={() => ''}
          />
          <Controller
            name="dtDesejadaCliente"
            control={control}
            rules={{
              validate: {
                min: (date) =>
                  moment(date)
                    .endOf('day')
                    .isAfter(
                      moment(FaturamentoEntrega.DataEntregaCalculada).startOf('day')
                    ) || 'Data fora do período válido.',
                max: (date) =>
                  moment(date)
                    .startOf('day')
                    .isBefore(moment(currentPrevDate).add(120, 'd').endOf('day')) ||
                  'Data fora do período válido.'
              }
            }}
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                label="Dt. desejada cliente"
                disabled={shouldDisable(ACTIONS_SCREENS.SAVE)}
                minDate={
                  moment(FaturamentoEntrega.DataEntregaCalculada) as unknown as string
                }
                maxDate={moment(currentPrevDate).add(120, 'd') as unknown as string}
                error={fieldState.error?.message}
              />
            )}
          />
          <DatePicker
            label="Dt. fatura combinada"
            value={FaturamentoEntrega.DataFaturamentoCombinado}
            disabled
            onChange={() => ''}
          />
          {isDirty && (
            <S.ActionsRow>
              <SlimButton
                content="Salvar"
                type="submit"
                variant="primary"
                loading={loadingSave}
              />
              <SlimButton
                content="Desfazer"
                type="button"
                variant="secondary"
                disabled={loadingSave}
                onClick={resetForm}
              />
            </S.ActionsRow>
          )}
        </S.Wrapper>
      )}
    </SectionCard>
  )
}
